import type { PartialBy } from '@ipfabric/utils/types';
import type { OmitNull } from '@ipfabric/utils/types/omitNull';
import type { ReactNode } from 'react';
import type { VariantProps } from 'tailwind-variants';
import type {
  InteractiveAtomRenderedAsAnchorProps,
  InteractiveAtomRenderedAsButtonProps,
  InteractiveAtomRenderedAsDivProps,
  InteractiveAtomRenderedAsLinkProps,
} from '../../utils/InteractiveAtomProps';
import type { listItemVariants } from './ListItem.styles';

export type ListItemVariantProps = OmitNull<
  Omit<VariantProps<typeof listItemVariants>, 'as' | 'kind'>
>;

type ListItemBaseProps = {
  dataTestId?: string;
  description?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;

  // selected common underlying element props for "official interface"
  className?: string;
} & ListItemVariantProps;

type WithLabelProps = {
  label: string;
  customContent?: never; // This ensures customContent cannot be used with the label
};

type WithCustomContentProps = {
  customContent: ReactNode;
  label?: never; // This ensures label cannot be used with customContent
};

export type ListItemContainerProps = PartialBy<
  InteractiveAtomRenderedAsDivProps<ListItemBaseProps>,
  'renderAs'
> &
  WithLabelProps;

export type ListItemAnchorProps =
  InteractiveAtomRenderedAsAnchorProps<ListItemBaseProps> & WithLabelProps;

export type ListItemLinkProps =
  InteractiveAtomRenderedAsLinkProps<ListItemBaseProps> & WithLabelProps;

export type ListItemButtonProps =
  InteractiveAtomRenderedAsButtonProps<ListItemBaseProps> & WithLabelProps;

export type ListItemWithCustomContentProps = PartialBy<
  InteractiveAtomRenderedAsDivProps<ListItemBaseProps>,
  'renderAs'
> &
  WithCustomContentProps;

export type ListItemProps =
  | ListItemAnchorProps
  | ListItemLinkProps
  | ListItemButtonProps
  | ListItemContainerProps
  | ListItemWithCustomContentProps;

export const isListItemWithCustomContent = (
  item: ListItemProps,
): item is ListItemWithCustomContentProps => {
  return (item as ListItemWithCustomContentProps).customContent !== undefined;
};
